import { PickedFile } from '@capawesome/capacitor-file-picker';
import { base64toBlob, blobToBase64, resizeBase64, resizeImage } from '../utils/data';
import { authFetch } from '../service/AuthService';
import { ContractType, RoleUtils, UserDoc } from 'common';

/*
export interface UserDoc {
  _id: string;
  _rev: string;
  type: string;
  first_name: string;
  last_name: string;
  company_role: string;
  company: string;
  roles: string[];
  projects: string[];
  email: string;
  email_verified: boolean;
  profile: any;
  isGuest?: boolean;
  guestInformation?: { projectId: string, sharedLinkId: string, url: string };
  color: string;
  phone: string;
  password_scheme:string;
  _attachments: { [key: string]: { content_type: string, revpos?: number, digest?: string, length?: number, data: string  } };
}
*/

export interface PermissionParams {
  roles: string[];
  projectId: string;
  feature: string;
  docId?: string;
}

export class PermissionsSet {
  private roles: string[];
  private projectId: string;
  private feature: string;
  private docId?: string;

  constructor(params: PermissionParams) {
    this.roles = params.roles;
    this.projectId = params.projectId;
    this.feature = params.feature;
    this.docId = params.docId;
  }

  // Propject Level
  get canReadProject(): boolean {
    return this.roles.includes(`p-${this.projectId}-read`);
  }
  get canWriteProject(): boolean {
    return this.roles.includes(`p-${this.projectId}-write`);
  }

  // Feature Level
  get featureReadRole(): string {
    return `p-${this.projectId}-${this.feature}-read`;
  }
  get featureWriteRole(): string {
    return `p-${this.projectId}-${this.feature}-write`;
  }
  get canReadFeature(): boolean {
    return this.canReadProject || this.roles.includes(this.featureReadRole);
  }
  get canWriteFeature(): boolean {
    return this.canWriteProject || this.roles.includes(this.featureWriteRole);
  }

  // Document Level, optional, not all features have a document level controls, e.g Schedule does not (yet).
  // This will primarily be handed in the proxy, so we don't generally need to check it here.
  get readDocumentRole(): string {
    return `p-${this.projectId}-${this.feature}-${this.docId}-read`;
  }
  get writeDocumentRole(): string {
    return `p-${this.projectId}-${this.feature}-${this.docId}-write`;
  }
  get canReadDocument(): boolean {
    return RoleUtils.hasDocumentReadAccess(`p-${this.projectId}-${this.feature}`, this.docId, this.roles);
  }
  get canWriteDocument(): boolean {
    return RoleUtils.hasDocumentWriteAccess(`p-${this.projectId}-${this.feature}`, this.docId, this.roles);
  }
  get canReadSomeDocuments(): boolean {
    return RoleUtils.hasSomeDocumentReadAccess(`p-${this.projectId}-${this.feature}`, this.roles);
  }

 // Convenience methods for checking if the user has read or write permissions for the feature.
  get canRead(): boolean {
    return this.canReadDocument;
  }
  get canWrite(): boolean {
    return this.canWriteDocument;
  }

  get readRole(): string {
    return this.docId ? this.readDocumentRole : this.featureReadRole;
  }
  get writeRole(): string {
    return this.docId ? this.writeDocumentRole : this.featureWriteRole;
  }
}
/*
export class ProjectPermissions {
  private roles: string[];
  private projectId: string;
  constructor(roles: string[], projectId: string) {
    this.roles = roles;
    this.projectId = projectId;
  }
  get canReadProject(): boolean {
    return this.roles.includes(`p-${this.projectId}`);
  }
  get canWriteProject(): boolean {
    return this.roles.includes(`p-${this.projectId}-write`);
  }

  get peoplePermissions() {
    return new PermissionsSet({ roles: this.roles, projectId: this.projectId, feature: 'people' });
  }
  get rfiPermissions() {
    return new PermissionsSet({ roles: this.roles, projectId: this.projectId, feature: 'rfis' });
  }
  get scrapbookPermissions() {
    return new PermissionsSet({ roles: this.roles, projectId: this.projectId, feature: 'schedules' });
  }
  get drawingsPermissions() {
    return new PermissionsSet({ roles: this.roles, projectId: this.projectId, feature: 'drawings' });
  }
  get submittalsPermissions() {
    return new PermissionsSet({ roles: this.roles, projectId: this.projectId, feature: 'submittals' });
  }
}*/

export class User {
  static readonly COLORS = [
    '#AD1457',
    '#6A1B9A',
    '#4527A0',
    '#1565C0',
    '#283593',
    '#0277BD',
    '#00838F',
    '#00695C',
    '#2E7D32',
    '#558B2F',
    // '#9E9D24',
    '#EF6C00',
    '#D84315',
    '#4E342E',
    '#D8FF00',
    '#4E002E',
  ];

  static async getUserDoc(userId: string): Promise<UserDoc> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/get-user?userId=' + userId, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + globalThis.user.token,
      },
    });

    if (!response.ok) {
      throw new Error((await response.json()).message);
    }

    return await response.json();
  }
  /*
  static async confirmAccessCode(code: string, password: string): Promise<UserSession> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/confirm-access-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + globalThis.user.token,
      },
      body: JSON.stringify({ code, password }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message);
    return data;
  }


  static async changePasswordFromCode(email: string, code: string, password: string): Promise<UserSession> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/change-password-from-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + (!!globalThis.user ? globalThis.user.token : ''),
      },
      body: JSON.stringify({ email, code, password }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message);
    return data;
  }

  static async updateUser(doc: UserDoc): Promise<UserSession> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/update-user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + globalThis.user.token,
      },
      body: JSON.stringify({ formData: doc }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error('Error updating user: ' + data.message);
    return data;
  }
    */

  public doc: UserDoc;
  private _pictureUrl: string;

  constructor(doc: UserDoc) {
    this.doc = doc;
  }

  get isAnonymous() {
    return !this.doc.email;
  }

  get pendingEmailConfirmation() {
    return this.doc.email && !this.doc.email_verified;
  }

  get isGuestxx() {
    return this.doc.email && !this.doc.email_verified;
  }

  get hasPassword() {
    return this.doc.has_password;
  }

  get isUser() {
    return this.doc.type == 'user';
  }

  get id() {
    return this.doc._id;
  }

  get roles(): string[] {
    return this.doc.roles;
  }

  private sessionColor: string;
  get color() {
    if (!this.sessionColor) {
      const color = User.COLORS[Math.floor(Math.random() * User.COLORS.length)];
      this.sessionColor = color;
    }
    return this.doc.color || this.sessionColor;
  }

  getRoles(projectId: string) {
    return this.doc.roles.filter(role => role.includes(`p-${projectId}`));
  }
/*
  getProjectPermissions(projectId: string): ProjectPermissions {
    return new ProjectPermissions(this.doc.roles, projectId);
  }*/

  getPermissions(projectId: string, feature: string, docId: string = null): PermissionsSet {
    return new PermissionsSet({ roles: this.doc.roles, projectId: projectId, feature: feature, docId: docId });
  }

  getSchedulePermissions(projectId: string): PermissionsSet {
    return new PermissionsSet({ roles: this.doc.roles, projectId: projectId, feature: 'schedules' });
  }

  getHoursTypePermissions(projectId: string): PermissionsSet {
    return new PermissionsSet({ roles: this.doc.roles, projectId: projectId, feature: 'type' });
  }

  getHoursLogPermissions(projectId: string): PermissionsSet {
    return new PermissionsSet({ roles: this.doc.roles, projectId: projectId, feature: 'log' });
  }

  getDigestPermissions(projectId: string): PermissionsSet {
    return new PermissionsSet({ roles: this.doc.roles, projectId: projectId, feature: 'sign-digest' });
  }

  getDrawingsPermissions(projectId: string): PermissionsSet {
    return new PermissionsSet({ roles: this.doc.roles, projectId: projectId, feature: 'drawings' });
  }

  getScrapbookPermissions(projectId: string): PermissionsSet {
    return new PermissionsSet({ roles: this.doc.roles, projectId: projectId, feature: 'scrapbooks' });
  }

  getRfiPermissions(projectId: string, rfiId: string = null): PermissionsSet {
    return new PermissionsSet({ roles: this.doc.roles, projectId: projectId, feature: 'rfis', docId: rfiId });
  }

  getSubmittalsPermissions(projectId: string, submittalId: string = null): PermissionsSet {
    return new PermissionsSet({ roles: this.doc.roles, projectId: projectId, feature: 'submittals', docId: submittalId });
  }

  getContractPermissions(projectId: string, contractId: string = null): PermissionsSet {
    return new PermissionsSet({ roles: this.doc.roles, projectId: projectId, feature: 'contracts', docId: contractId });
  }

  hasRole(role: string) {
    return this.doc.roles.includes(role);
  }

  hasOneOfRoles(roles: string[]): boolean {
    return this.doc.roles.some(role => roles.includes(role));
  }

  canAdministerProject(projectId: string) {
    return this.doc.roles.includes(`p-${projectId}-write`);
  }

  canDoBasicThings(projectId: string) {
    return this.canAdministerProject(projectId) || this.doc.roles.includes(`p-${projectId}-base`);
  }

  canAdministerPeople(projectId: string) {
    return this.doc.roles.includes(`p-${projectId}-write`) || this.doc.roles.includes(`p-${projectId}-people-write`);
  }

  get projectIds(): string[] {
    if (!this.doc.projects || this.doc.projects.length == 0) return ['demo'];
    return [...new Set(this.doc.projects)];
  }

  get initials() {
    const fname = this.doc.first_name;
    const lname = this.doc.last_name;

    if (!fname) return null;

    let firstLetter: string = fname ? fname.charAt(0) : '';
    let secondLetter: string = lname ? lname.charAt(0) : fname.charAt(1);
    return String(firstLetter + secondLetter).toUpperCase();
  }

  get fullName() {
    return this.doc.first_name ? this.doc.first_name + ' ' + this.doc.last_name : null;
  }

  get pictureUrl(): string {
    if (this._pictureUrl) return this._pictureUrl;
    if (this.doc.image && this.doc.image.data) return `data:${this.doc.image.mimeType};base64,${this.doc.image.data}`;
    if (!this.doc._attachments || !this.doc._attachments.hasOwnProperty('profilePicture') || !this.doc._attachments['profilePicture'].data) return null;

    const attachment = this.doc._attachments['profilePicture'];
    const contentType = attachment.content_type;
    const blob = base64toBlob(attachment.data, contentType);
    this._pictureUrl = URL.createObjectURL(blob);
    return this._pictureUrl;
  }

  async setSelectedFile(value: PickedFile) {
    if (value) {
      const blob = await resizeImage(value.blob, 86, 86, 'image/webp');
      const base64 = await blobToBase64(blob);
      this.doc._attachments = this.doc._attachments || {};
      this.doc._attachments['profilePicture'] = { content_type: blob.type, data: base64 };
    }
    this._pictureUrl = null;
  }

  async setProfilePictureFromFile(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        this.doc.image = await resizeBase64(reader.result as string, 86, 86);
        resolve();
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }
}
